import { useState } from "react";
import { X, BookOpen, User, Calendar, ArrowDownCircle } from "lucide-react";

export default function ReturnModal({
  open,
  loan,
  bookTitle,
  onClose,
  onReturnSuccess,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open || !loan) return null;

  // Safe data fallbacks to support flexible property shapes across components
  const resolvedTitle = loan.book?.title || loan.book_title || bookTitle || "Untitled Catalog Item";
  const borrowerName = loan.borrower || loan.patron_name || "Assigned Patron";
  const systemBarcode = loan.barcode || loan.book?.barcode || "N/A";
  
  const formatDisplayDate = (dateInput) => {
    if (!dateInput) return "—";
    try {
      const dateObj = new Date(dateInput);
      return isNaN(dateObj.getTime()) 
        ? "—" 
        : dateObj.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const handleConfirmReturn = async () => {
    try {
      setIsSubmitting(true);
      // Execute the callback passed down from your parent container context
      await onReturnSuccess(loan);
    } catch {
      // Error logging can be handled inside your main callback or right here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-950/80 font-dynamic-title backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#1a1613] rounded-2xl shadow-2xl max-w-md w-full border border-[#2e2720] overflow-hidden transform transition-all animate-scaleUp">
        
        {/* Terminal Header Layout */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl">
              <ArrowDownCircle className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100">Verify Check-In</h3>
              <p className="text-xs text-stone-400">Confirm physical possession of the material asset.</p>
            </div>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            disabled={isSubmitting}
            className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1.5 rounded-xl transition disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Core Sheet Context Area */}
        <div className="p-6 space-y-4">
          <p className="text-xs text-stone-300 leading-relaxed">
            Are you sure you want to process this transaction return? Verify that the barcode matches the physical book label below:
          </p>

          {/* Staged Inventory Resource Card */}
          <div className="bg-[#131110] border border-[#2e2720] rounded-xl p-4 space-y-3">
            
            {/* Title Block row */}
            <div className="flex items-start gap-3">
              <BookOpen className="h-4 w-4 text-amber-500/70 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">Catalog Title</p>
                <h4 className="text-sm font-bold text-stone-100 truncate mt-0.5">{resolvedTitle}</h4>
              </div>
            </div>

            {/* Micro Details Metadata Split Rows */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#2e2720] text-xs">
              
              <div className="space-y-0.5 min-w-0">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wide block">Borrower</span>
                <div className="flex items-center gap-1.5 text-stone-300 font-medium">
                  <User className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                  <span className="truncate">{borrowerName}</span>
                </div>
              </div>

              <div className="space-y-0.5 font-mono">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wide block font-sans">System Barcode</span>
                <span className="inline-block bg-[#1a1613] border border-[#2e2720] text-stone-300 px-2 py-0.5 rounded-md text-[11px] font-bold">
                  {systemBarcode}
                </span>
              </div>

              {loan.due_date && (
                <div className="space-y-0.5 col-span-2 pt-1">
                  <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wide block">Expected Due Target</span>
                  <div className="flex items-center gap-1.5 text-stone-300 font-medium">
                    <Calendar className="h-3.5 w-3.5 text-stone-500 shrink-0" />
                    <span className="font-mono">{formatDisplayDate(loan.due_date)}</span>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Modal Commands Footer Actions Area */}
        <div className="px-6 py-4 bg-[#131110] flex items-center justify-end gap-2 border-t border-[#2e2720]">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-[#2e2720] font-mono bg-[#131110] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] font-semibold text-xs rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>
          
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleConfirmReturn}
            className="px-5 py-2 bg-emerald-500 font-mono hover:bg-emerald-400 disabled:opacity-50 text-stone-950 font-bold text-xs rounded-xl shadow transition"
          >
            {isSubmitting ? "Processing Check-In..." : "Confirm Return"}
          </button>
        </div>

      </div>
    </div>
  );
}